import { useState } from 'react';
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData';
import { showToast } from '@/components/layout/Toast';
import type { CampanaEmail, CampanaProspecto } from '@/lib/types';
import { PageHeader } from '@/components/ui';

/*
  Relleno y texto van juntos: --success, --warning, --danger y --info son
  oscuros en el tema claro y pasteles en el oscuro, asi que el blanco encima
  solo se lee en uno de los dos (1.9:1 medido sobre el verde oscuro). Los
  rellenos de marca y el gris si llevan blanco en ambos.
*/
type ColorBadge = { bg: string; fg: string };
const BADGE_NEUTRO: ColorBadge = { bg: 'var(--text-muted)', fg: 'var(--text-on-primary)' };
const badgeColors: Record<string, ColorBadge> = {
  pendiente: BADGE_NEUTRO,
  enviado: { bg: 'var(--info)', fg: 'var(--on-info)' },
  abierto: { bg: 'var(--success)', fg: 'var(--on-success)' },
  click: { bg: 'var(--primary-light)', fg: 'var(--text-on-primary)' },
  respondido: { bg: 'var(--brand-500)', fg: 'var(--text-on-primary)' },
  rebotado: { bg: 'var(--warning)', fg: 'var(--on-warning)' },
};

export default function CampanasEmail() {
  const api = useApi();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [prospects, setProspects] = useState<CampanaProspecto[]>([]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showProspectModal, setShowProspectModal] = useState(false);
  const [prospectTab, setProspectTab] = useState<'manual' | 'csv'>('manual');
  const [testEmail, setTestEmail] = useState('');

  const [newCampaign, setNewCampaign] = useState({
    nombre: '',
    asunto: '',
    contenido_html: '',
    envios_diarios: 20,
    hora_envio: '09:00',
    whatsapp_numero: '+51986252983',
    whatsapp_mensaje: '',
    booking_url: '',
  });

  const [manualProspect, setManualProspect] = useState({
    nombre: '',
    email: '',
    empresa: '',
  });

  const [csvText, setCsvText] = useState('');

  // ── Campañas (en caché: volver a la pestaña no las vuelve a pedir) ──
  // Misma clave que el tablero de Marketing, que lee la misma lista.
  const { data: campaigns = [], isLoading: loading, refrescar: fetchCampaigns } = useCachedData(
    ['campanas'],
    () => api.get<CampanaEmail[]>('/api/campaigns').catch(() => {
      showToast('Error al cargar campañas', 'error');
      return [] as CampanaEmail[];
    }),
  );

  const fetchProspects = async (id: number) => {
    try {
      const data = await api.get<CampanaProspecto[]>(`/api/campaigns/${id}/prospectos`);
      setProspects(data);
    } catch (err) {
      showToast('Error al cargar prospectos', 'error');
    }
  };


  // ── Campaign actions ──
  const handleCreateCampaign = async () => {
    try {
      await api.post('/api/campaigns', newCampaign);
      showToast('Campaña creada', 'success');
      setShowNewModal(false);
      setNewCampaign({
        nombre: '',
        asunto: '',
        contenido_html: '',
        envios_diarios: 20,
        hora_envio: '09:00',
        whatsapp_numero: '+51986252983',
        whatsapp_mensaje: '',
        booking_url: '',
      });
      fetchCampaigns();
    } catch (err) {
      showToast('Error al crear campaña', 'error');
    }
  };

  const handleToggleStatus = async (campaign: CampanaEmail) => {
    const next = campaign.estado === 'activa' ? 'pausada' : 'activa';
    try {
      await api.put(`/api/campaigns/${campaign.id}`, { estado: next });
      showToast(`Campaña ${next}`, 'success');
      fetchCampaigns();
    } catch (err) {
      showToast('Error al cambiar estado', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar esta campaña?')) return;
    try {
      await api.del(`/api/campaigns/${id}`);
      showToast('Campaña eliminada', 'success');
      setSelectedId(null);
      fetchCampaigns();
    } catch (err) {
      showToast('Error al eliminar', 'error');
    }
  };

  const handleAddProspect = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/api/campaigns/${selectedId}/prospectos`, manualProspect);
      showToast('Prospecto agregado', 'success');
      setManualProspect({ nombre: '', email: '', empresa: '' });
      fetchProspects(selectedId);
      fetchCampaigns();
    } catch (err) {
      showToast('Error al agregar prospecto', 'error');
    }
  };

  const handleImportCsv = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/api/campaigns/${selectedId}/prospectos/import`, { csv: csvText });
      showToast('Prospectos importados', 'success');
      setCsvText('');
      setShowProspectModal(false);
      fetchProspects(selectedId);
      fetchCampaigns();
    } catch (err) {
      showToast('Error al importar', 'error');
    }
  };

  const handleTestSend = async () => {
    if (!selectedId) return;
    try {
      await api.post(`/api/campaigns/${selectedId}/test-send`, { email: testEmail });
      showToast('Email de prueba enviado', 'success');
      setTestEmail('');
    } catch (err) {
      showToast('Error al enviar prueba', 'error');
    }
  };

  const openDetail = (id: number) => {
    setSelectedId(id);
    fetchProspects(id);
  };

  const selected = campaigns.find((c) => c.id === selectedId) || null;

  // ── Shared styles ──
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text-primary)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    color: 'var(--text-muted)',
    marginBottom: 6,
  };

  const btnPrimary: React.CSSProperties = {
    padding: '10px 20px',
    background: 'var(--brand-500)',
    color: 'var(--text-on-primary)',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  };

  const btnGhost: React.CSSProperties = {
    padding: '10px 20px',
    background: 'transparent',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer',
  };

  // ── Badge helper ──
  const renderBadge = (estado: string) => {
    const c = badgeColors[estado] || BADGE_NEUTRO;
    return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: c.bg,
        color: c.fg,
      }}
    >
      {estado}
    </span>
    );
  };

  // ══════════════════════════════════════════════
  // LIST VIEW
  // ══════════════════════════════════════════════
  if (selectedId === null) {
    return (
      <div style={{ padding: 32 }}>
        <PageHeader
          breadcrumbs={[{ label: 'Marketing' }, { label: 'Campañas' }]}
          meta={`${campaigns.length} campaña${campaigns.length !== 1 ? 's' : ''}`}
          actions={
            <button style={btnPrimary} onClick={() => setShowNewModal(true)}>
              + Nueva Campaña
            </button>
          }
        />

        {/* Campaign cards grid */}
        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>Cargando...</p>
        ) : campaigns.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No hay campañas aún. Crea la primera.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
            {campaigns.map((c) => {
              const total = c.total_prospectos || 0;
              const pct = total > 0 ? Math.round((c.total_enviados / total) * 100) : 0;
              return (
                <div
                  key={c.id}
                  onClick={() => openDetail(c.id)}
                  style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: 24,
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--brand-500) 40%, transparent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--veil-06)')}
                >
                  {/* Card header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{c.nombre}</span>
                    {renderBadge(c.estado)}
                  </div>

                  {/* Progress bar */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Progreso</span>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {c.total_enviados}/{total} ({pct}%)
                      </span>
                    </div>
                    <div style={{ height: 6, background: 'var(--veil-06)', borderRadius: 3 }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${pct}%`,
                          background: 'linear-gradient(90deg, #342CDE, #4F7FE8)',
                          borderRadius: 3,
                          transition: 'width 0.3s',
                        }}
                      />
                    </div>
                  </div>

                  {/* 4 mini stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                    {[
                      { label: 'Enviados', value: c.total_enviados },
                      { label: 'Abiertos', value: c.total_abiertos },
                      { label: 'Clicks', value: c.total_clicks },
                      { label: 'Leads', value: c.total_leads },
                    ].map((s) => (
                      <div
                        key={s.label}
                        style={{
                          textAlign: 'center',
                          padding: '10px 4px',
                          background: 'var(--veil-03)',
                          borderRadius: 8,
                        }}
                      >
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{s.value}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)' }}>
                    <span>⏰ {c.hora_envio}</span>
                    <span>{c.envios_diarios}/día</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* New Campaign Modal */}
        {showNewModal && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'var(--scrim)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowNewModal(false)}
          >
            <div
              style={{ maxWidth: 560, width: '90%', background: 'var(--bg-modal)', borderRadius: 16, padding: 32 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 24px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Nueva Campaña</h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Nombre</label>
                  <input
                    style={inputStyle}
                    value={newCampaign.nombre}
                    onChange={(e) => setNewCampaign({ ...newCampaign, nombre: e.target.value })}
                    placeholder="Nombre de la campaña"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Asunto</label>
                  <input
                    style={inputStyle}
                    value={newCampaign.asunto}
                    onChange={(e) => setNewCampaign({ ...newCampaign, asunto: e.target.value })}
                    placeholder="Asunto del email"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Contenido HTML</label>
                  <textarea
                    style={{ ...inputStyle, resize: 'vertical' }}
                    rows={6}
                    value={newCampaign.contenido_html}
                    onChange={(e) => setNewCampaign({ ...newCampaign, contenido_html: e.target.value })}
                    placeholder="HTML del email"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Envíos diarios</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={newCampaign.envios_diarios}
                      onChange={(e) => setNewCampaign({ ...newCampaign, envios_diarios: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Hora de envío</label>
                    <input
                      style={inputStyle}
                      type="time"
                      value={newCampaign.hora_envio}
                      onChange={(e) => setNewCampaign({ ...newCampaign, hora_envio: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>WhatsApp número</label>
                  <input
                    style={inputStyle}
                    value={newCampaign.whatsapp_numero}
                    onChange={(e) => setNewCampaign({ ...newCampaign, whatsapp_numero: e.target.value })}
                    placeholder="+51986252983"
                  />
                </div>
                <div>
                  <label style={labelStyle}>WhatsApp mensaje</label>
                  <textarea
                    style={{ ...inputStyle, resize: 'vertical' }}
                    rows={3}
                    value={newCampaign.whatsapp_mensaje}
                    onChange={(e) => setNewCampaign({ ...newCampaign, whatsapp_mensaje: e.target.value })}
                    placeholder="Mensaje de seguimiento WhatsApp"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Booking URL</label>
                  <input
                    style={inputStyle}
                    value={newCampaign.booking_url}
                    onChange={(e) => setNewCampaign({ ...newCampaign, booking_url: e.target.value })}
                    placeholder="https://calendly.com/..."
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                <button style={btnGhost} onClick={() => setShowNewModal(false)}>
                  Cancelar
                </button>
                <button style={btnPrimary} onClick={handleCreateCampaign}>
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════
  // DETAIL VIEW
  // ══════════════════════════════════════════════
  if (!selected) return null;

  const total = selected.total_prospectos || 0;
  const funnelData = [
    { label: 'Enviados', value: selected.total_enviados, color: 'var(--info)' },
    { label: 'Abiertos', value: selected.total_abiertos, color: 'var(--success)' },
    { label: 'Clicks', value: selected.total_clicks, color: 'var(--primary-light)' },
    { label: 'Leads', value: selected.total_leads, color: 'var(--brand-500)' },
  ];

  return (
    <div style={{ padding: 32 }}>
      <PageHeader
        breadcrumbs={[
          { label: 'Marketing' },
          { label: 'Campañas', onClick: () => setSelectedId(null) },
          { label: selected.nombre },
        ]}
        meta={renderBadge(selected.estado)}
        actions={
          <>
            <button
              style={{
                ...btnPrimary,
                background: selected.estado === 'activa' ? 'var(--warning)' : 'var(--success)',
                color: selected.estado === 'activa' ? 'var(--on-warning)' : 'var(--on-success)',
              }}
              onClick={() => handleToggleStatus(selected)}
            >
              {selected.estado === 'activa' ? 'Pausar' : 'Activar'}
            </button>
            <button
              style={{ ...btnGhost, color: 'var(--danger)', borderColor: 'var(--danger)' }}
              onClick={() => handleDelete(selected.id)}
            >
              Eliminar
            </button>
          </>
        }
      />

      {/* Funnel stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        {funnelData.map((f, i) => {
          const pct = total > 0 ? Math.round((f.value / total) * 100) : 0;
          return (
            <div
              key={f.label}
              style={{
                position: 'relative',
                padding: 20,
                borderRadius: 12,
                background: `${f.color}15`,
                border: `1px solid ${f.color}30`,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 28, fontWeight: 700, color: f.color }}>{f.value}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{f.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{pct}%</div>
              {i < funnelData.length - 1 && (
                <span
                  style={{
                    position: 'absolute',
                    right: -14,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--veil-15)',
                    fontSize: 18,
                  }}
                >
                  →
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Prospectos section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>Prospectos</h2>
        <button style={btnPrimary} onClick={() => setShowProspectModal(true)}>
          + Agregar Prospectos
        </button>
      </div>

      {/* Prospectos table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: 32 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Email', 'Nombre', 'Empresa', 'Estado', 'Enviado', 'Abierto'].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--text-muted)',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {prospects.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}
                >
                  No hay prospectos aún
                </td>
              </tr>
            ) : (
              prospects.map((p) => (
                <tr key={p.id}>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}>
                    {p.email}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}>
                    {p.nombre || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}>
                    {p.empresa || '—'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14, borderBottom: '1px solid var(--border)' }}>
                    {renderBadge(p.estado)}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}>
                    {p.enviado_at ? new Date(p.enviado_at).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}>
                    {p.abierto_at ? new Date(p.abierto_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Enviar Prueba section */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span style={{ fontSize: 14, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Enviar Prueba:</span>
        <input
          style={{ ...inputStyle, flex: 1 }}
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="email@ejemplo.com"
        />
        <button style={btnPrimary} onClick={handleTestSend}>
          Enviar
        </button>
      </div>

      {/* Prospect Modal */}
      {showProspectModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'var(--scrim)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowProspectModal(false)}
        >
          <div
            style={{ maxWidth: 560, width: '90%', background: 'var(--bg-modal)', borderRadius: 16, padding: 32 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Agregar Prospectos</h2>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid var(--border)' }}>
              {(['manual', 'csv'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setProspectTab(tab)}
                  style={{
                    padding: '10px 20px',
                    background: 'none',
                    border: 'none',
                    borderBottom: prospectTab === tab ? '2px solid var(--brand-500)' : '2px solid transparent',
                    color: prospectTab === tab ? 'var(--text-primary)' : 'var(--text-muted)',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}
                >
                  {tab === 'manual' ? 'Manual' : 'Importar CSV'}
                </button>
              ))}
            </div>

            {prospectTab === 'manual' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Nombre</label>
                  <input
                    style={inputStyle}
                    value={manualProspect.nombre}
                    onChange={(e) => setManualProspect({ ...manualProspect, nombre: e.target.value })}
                    placeholder="Nombre del prospecto"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input
                    style={inputStyle}
                    value={manualProspect.email}
                    onChange={(e) => setManualProspect({ ...manualProspect, email: e.target.value })}
                    placeholder="email@empresa.com"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Empresa</label>
                  <input
                    style={inputStyle}
                    value={manualProspect.empresa}
                    onChange={(e) => setManualProspect({ ...manualProspect, empresa: e.target.value })}
                    placeholder="Nombre de la empresa"
                  />
                </div>
                <button style={{ ...btnPrimary, marginTop: 8 }} onClick={handleAddProspect}>
                  Agregar
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>CSV (email, nombre, empresa — uno por línea)</label>
                  <textarea
                    style={{ ...inputStyle, resize: 'vertical' }}
                    rows={8}
                    value={csvText}
                    onChange={(e) => setCsvText(e.target.value)}
                    placeholder="email@empresa.com, Juan Pérez, Acme Corp&#10;otro@empresa.com, María López, Tech SA"
                  />
                </div>
                <button style={{ ...btnPrimary, marginTop: 8 }} onClick={handleImportCsv}>
                  Importar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
